/**
 * Mode 3 — Vector (SVG + DXF) export, brief 01-export.md.
 *
 * Every selected symmetry copy's trace-layer paths are flattened through that
 * copy's placement matrix into ABSOLUTE coordinates (no `<g transform=...>`,
 * no DXF INSERT/block instancing) — the brief's explicit Cricut-compatibility
 * requirement.
 */
import { DxfWriter, point2d } from '@tarikjabiri/dxf';
import { isIdentityTransform, transformToMatrix, type Mat } from '@/geometry/affine';
import { flattenPathToPolylines, pathBounds, transformPathD } from '@/geometry/svgPath';
import { downloadText } from '@/services/download';
import { exportFilename } from '@/services/exportFilename';
import { useLayersStore } from '@/stores/layers';
import { usePatternStore } from '@/stores/pattern';
import type { TileTransform } from '@/patterns/types';

export interface VectorCopyCandidate {
  /** stable key for UI selection sets / v-for */
  key: string;
  transform: TileTransform;
  matrix: Mat;
  isMain: boolean;
}

/**
 * Candidate copies for the Mode 3 picker — the main (identity) copy plus its
 * nearest neighbours by translation distance, capped to a manageable picker
 * size. `rings`/`max` intentionally modest: this is a "pick a handful of
 * copies for a stamp set" UI (brief's own example), not an exhaustive tiling.
 */
export function vectorCopyCandidates(rings = 2, max = 24): VectorCopyCandidate[] {
  const patternStore = usePatternStore();
  const placements = patternStore.currentVariant.transformsInRange(rings);
  const withMatrix = placements.map((t) => ({ t, m: transformToMatrix(t) }));
  withMatrix.sort((a, b) => Math.hypot(a.m[4], a.m[5]) - Math.hypot(b.m[4], b.m[5]));
  return withMatrix.slice(0, max).map(({ t, m }, i) => ({
    key: `${i}-${m.map((v) => v.toFixed(4)).join(',')}`,
    transform: t,
    matrix: m,
    isMain: isIdentityTransform(t),
  }));
}

interface FlattenedCopy {
  index: number;
  /** absolute-coordinate `d` strings, one per visible trace layer */
  paths: string[];
}

function flattenSelectedCopies(copies: readonly VectorCopyCandidate[]): FlattenedCopy[] {
  const layers = useLayersStore();
  const visible = layers.traceLayers.filter((l) => l.visible && l.d);
  return copies.map((copy, index) => ({
    index,
    paths: visible.map((l) => transformPathD(l.d, copy.matrix)),
  }));
}

/** Clean, flat SVG — no `<use>`, no nested `<g transform>`, one top-level
 *  `<g id="copy-N">` per selected copy, viewBox fit to the actual content. */
export function buildSvg(copies: readonly VectorCopyCandidate[]): string {
  const flattened = flattenSelectedCopies(copies);
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;

  const groups = flattened.map((c) => {
    const paths = c.paths.map((d) => {
      const b = pathBounds(d);
      if (b) {
        minX = Math.min(minX, b.minX);
        minY = Math.min(minY, b.minY);
        maxX = Math.max(maxX, b.maxX);
        maxY = Math.max(maxY, b.maxY);
      }
      return `    <path d="${d}" fill="none" stroke="#000000" stroke-width="0.01" />`;
    });
    return `  <g id="copy-${c.index}">\n${paths.join('\n')}\n  </g>`;
  });

  if (!Number.isFinite(minX)) {
    minX = 0;
    minY = 0;
    maxX = 1;
    maxY = 1;
  }
  const pad = Math.max(maxX - minX, maxY - minY, 1) * 0.02;
  const vb = `${(minX - pad).toFixed(4)} ${(minY - pad).toFixed(4)} ${(maxX - minX + 2 * pad).toFixed(4)} ${(maxY - minY + 2 * pad).toFixed(4)}`;

  return (
    `<?xml version="1.0" encoding="UTF-8" standalone="no"?>\n` +
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="${vb}">\n${groups.join('\n')}\n</svg>\n`
  );
}

/** DXF R14/2000 ($ACADVER AC1015 = AutoCAD 2000) — Cricut/Silhouette-safe. Each
 *  copy is its own named layer; curves are flattened to LWPOLYLINE since a
 *  dense polyline is the entity type cut software reads reliably (a native
 *  cubic-Bézier path entity is not universally supported there). */
export function buildDxf(copies: readonly VectorCopyCandidate[]): string {
  const flattened = flattenSelectedCopies(copies);
  const dxf = new DxfWriter();
  dxf.setVariable('$ACADVER', { 1: 'AC1015' });

  for (const c of flattened) {
    const layerName = `copy-${c.index}`;
    dxf.addLayer(layerName, 7, 'CONTINUOUS');
    for (const d of c.paths) {
      for (const polyline of flattenPathToPolylines(d)) {
        if (polyline.length < 2) continue;
        dxf.addLWPolyline(
          polyline.map(([x, y]) => ({ point: point2d(x, y) })),
          { layerName },
        );
      }
    }
  }

  return dxf.stringify();
}

export function exportVector(copies: readonly VectorCopyCandidate[]): void {
  downloadText(buildSvg(copies), exportFilename('svg'), 'image/svg+xml');
  downloadText(buildDxf(copies), exportFilename('dxf'), 'application/dxf');
}
